import { Controller } from "@hotwired/stimulus";
import jsonLogic from "json-logic-js";

// A project's questions.
//
// Evaluates each question's showIf condition sequentially, building up
// responses as questions are answered. Shows all answered questions plus
// the first unanswered relevant question.
export default class extends Controller {
  static targets = ["submit"];
  static values = { questionnaireItemsCount: Number };
  static outlets = ["questionnaire-item"];

  #connectedQuestionnaireItemOutletsCount;
  #hasSuccessor = false;

  initialize() {
    this.#connectedQuestionnaireItemOutletsCount = 0;
  }

  questionnaireItemOutletConnected() {
    ++this.#connectedQuestionnaireItemOutletsCount;

    if (this.#isReady()) this.update();
  }

  update(event) {
    this.#toggleQuestionnaireItems();
    if (!this.#isTyping(event)) this.#focus();
    this.#toggleSubmit();

    this.scroll();
  }

  // Waits for the browser to finish layout and paint before scrolling to the bottom.
  scroll() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.element.scrollIntoView({ block: "end", inline: "nearest", behavior: "smooth" });
      });
    });
  }

  /* Private */

  get #shownQuestionnaireItemOutlets() {
    return this.questionnaireItemOutlets.filter((o) => o.isShown());
  }

  #isReady() {
    return this.#connectedQuestionnaireItemOutletsCount == this.questionnaireItemsCountValue;
  }

  #isComplete() {
    const shown = this.#shownQuestionnaireItemOutlets;
    if (shown.length === 0) return false;

    const last = shown.at(-1);

    // A terminal Continue question goes straight to Submit — the user submits
    // the form directly and native validation catches an empty value, so we
    // don't make them press Continue and then Submit. The one exception is
    // when a later, still-hidden question gates its relevance on this
    // question's answer: there we need the Continue press to commit the answer
    // before we can tell whether to reveal that question or show Submit.
    if (last.hasContinue && !this.#hasConditionalSuccessor(last)) {
      return shown.slice(0, -1).every((o) => o.hasValue());
    }

    return shown.every((o) => o.hasValue());
  }

  #hasConditionalSuccessor(outlet) {
    const index = this.questionnaireItemOutlets.indexOf(outlet);
    return this.questionnaireItemOutlets
      .slice(index + 1)
      .some((o) => this.#showIfReferences(o.showIfValue, outlet.key));
  }

  #showIfReferences(showIf, key) {
    if (Array.isArray(showIf)) return showIf.some((v) => this.#showIfReferences(v, key));
    if (showIf && typeof showIf === "object") {
      if (showIf.var === key) return true;
      return Object.values(showIf).some((v) => this.#showIfReferences(v, key));
    }
    return false;
  }

  // Shows all answered relevant questions plus the first unanswered relevant
  // question. Evaluates each question's showIf against the responses collected
  // so far, so conditions can only reference earlier questions.
  #toggleQuestionnaireItems() {
    let responses = {};
    let firstUnansweredSeen = false;
    this.#hasSuccessor = false;

    this.questionnaireItemOutlets.forEach((outlet) => {
      const relevant = jsonLogic.apply(outlet.showIfValue, responses);

      if (!relevant || firstUnansweredSeen) {
        if (relevant && firstUnansweredSeen) this.#hasSuccessor = true;
        outlet.toggle(false);
        return;
      }

      outlet.toggle(true);

      if (outlet.hasValue()) {
        responses[outlet.key] = outlet.value;
      } else {
        firstUnansweredSeen = true;
      }
    });
  }

  #toggleSubmit() {
    const shown = this.#shownQuestionnaireItemOutlets;
    const complete = this.#isComplete();
    const showSubmit = complete && !this.#hasSuccessor;

    this.submitTarget.classList.toggle("hidden", !showSubmit);
    if (showSubmit) shown.at(-1)?.hideContinue();
  }

  // A question answered by typing (systems) updates on every keystroke, and
  // may reveal the next question, but the user isn't done with it yet.
  #isTyping(event) {
    return event?.type === "input" && !["radio", "checkbox"].includes(event.target.type);
  }

  // Auto-focuses the newly revealed last question, unless the user is already
  // in it, so they aren't moved back to its first input.
  #focus() {
    const last = this.#shownQuestionnaireItemOutlets.at(-1);
    if (!last || last.element.contains(document.activeElement)) return;

    last.focus();
  }
}
